import * as React from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, User, KeyRound, Check, Loader, InfoIcon } from "lucide-react";
import {
  InputGroup,
  InputGroupInput,
  InputGroupAddon,
  InputGroupButton,
} from "@/taskpane/components/ui/input-group";
import { Button } from "@/taskpane/components/ui/button";
import {
  Field,
  FieldContent,
  FieldError,
  FieldGroup,

} from "@/taskpane/components/ui/field";
import useLogin from "@/hooks/useLogin";
import { useAuth } from "@/providers/AuthProvider";
import OnboardingHeader from "../OnboardingHeader";
import MarketingInfo from "./MarketingInfo";

// Simplified Zod validation schema
const loginSchema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginProps {
  onShowForgotPassword: () => void;
}

const Login: React.FC<LoginProps> = ({ onShowForgotPassword }) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const loginMutation = useLogin();
  const { isAuthenticated } = useAuth();

  const {
    control,
    handleSubmit,
    formState: { },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = (data: LoginFormData) => {
    loginMutation.mutate({
      username: data.email,
      password: data.password,
      is_addon: true,
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // If already authenticated, this component should not be rendered.
  // This check is primarily for development/testing, as App.tsx should prevent this.
  if (isAuthenticated) {
    return null; // Or a loading spinner, or redirect logic if this component is directly rendered
  }

  return (
    <div className="flex flex-col gap-4 w-full max-w-md mx-auto">
      <OnboardingHeader />
      <p className="font-medium text-sm text-center text-neutral-400 m-0">
        The Gold Standard in Legal Productivity.
      </p>

      <section
        className="flex flex-col items-center gap-y-4 w-full"
        role="main"
        aria-labelledby="login-heading"
        aria-describedby="login-description"
      >
        <div>
          <h2 id="login-heading" className="text-xl font-medium text-center m-0 block mb-2">
            Sign In
          </h2>
          <p
            id="login-description"
            className="text-sm font-normal text-neutral-400 text-center mb-4 block"
          >
            Enter your credentials to access
            <br />AI Legal Assistant
          </p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-3 w-full"
          noValidate
          aria-label="Sign in form"
        >
          <FieldGroup className="gap-4">
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid} className="gap-1">
                  <FieldContent>
                    <InputGroup aria-invalid={fieldState.invalid}>
                      <InputGroupAddon>
                        <User className="text-neutral-200" />
                      </InputGroupAddon>
                      <InputGroupInput
                        type="email"
                        id="email"
                        placeholder="Enter your email"
                        {...field}
                        disabled={loginMutation.isPending}
                        aria-describedby={fieldState.invalid ? "email-error" : undefined}
                        aria-invalid={fieldState.invalid}
                        autoComplete="email"
                        className="placeholder:text-neutral-500 text-sm py-3 px-4"
                      />
                    </InputGroup>
                    {fieldState.invalid && (
                      <div className="flex items-center gap-1 mt-1" id="email-error">
                        <InfoIcon className="inline size-3 text-danger-200" />
                        <FieldError className="leading-4" errors={[fieldState.error]} />
                      </div>
                    )}
                  </FieldContent>
                </Field>
              )}
            />
            <Controller
              name="password"
              control={control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid} className="gap-1">
                  <FieldContent>
                    <InputGroup aria-invalid={fieldState.invalid}>
                      <InputGroupAddon>
                        <KeyRound className="text-neutral-200" />
                      </InputGroupAddon>
                      <InputGroupInput
                        type={showPassword ? "text" : "password"}
                        id="password"
                        placeholder="Enter your password"
                        {...field}
                        disabled={loginMutation.isPending}
                        aria-describedby={fieldState.invalid ? "password-error" : undefined}
                        aria-invalid={fieldState.invalid}
                        autoComplete="current-password"
                        className="placeholder:text-neutral-500 text-sm py-3 px-4"
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupButton
                          onClick={togglePasswordVisibility}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          aria-pressed={showPassword}
                          disabled={loginMutation.isPending}
                        >
                          {showPassword ? <EyeOff /> : <Eye />}
                        </InputGroupButton>
                      </InputGroupAddon>
                    </InputGroup>
                    {fieldState.invalid && (
                      <div className="flex items-center gap-1 mt-1" id="password-error">
                        <InfoIcon className="inline size-3 text-danger-200" />
                        <FieldError className="leading-4" errors={[fieldState.error]} />
                      </div>
                    )}
                  </FieldContent>
                </Field>
              )}
            />
          </FieldGroup>

          <Button
            variant={"link"}
            type="button"
            className="text-primary-300 justify-start p-0 h-auto leading-5 self-start"
            onClick={(e) => {
              e.preventDefault();
              onShowForgotPassword();
            }}
          >
            Forgot Password?
          </Button>

          <Button
            type="submit"
            className="mt-2"
            size={"lg"}
            disabled={loginMutation.isPending}
            aria-describedby={loginMutation.isError ? "login-error" : undefined}
          >
            {loginMutation.isPending ? (
              <>
                <Loader className="animate-spin" />
                Signing in...
              </>
            ) : loginMutation.isSuccess ? (
              <>
                <Check />
                Signed in
              </>
            ) : (
              "Sign In"
            )}
          </Button>

          <p className="text-xs text-center text-neutral-400 mt-2">
            By signing in you accept our{" "}
            <a href="https://legalassistant.au/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="text-primary-300 hover:underline">
              terms and conditions
            </a>
            .
          </p>

          <div className="flex flex-col items-center gap-3 mt-6">
            <p className="text-sm text-neutral-400">Don't have an account?</p>
            <Button
              type="button"
              className="w-full bg-success-200 hover:bg-success-200/90 text-white font-medium"
              size={"lg"}
              onClick={() => window.open(`${process.env.APP_FRONTEND_URL}/free-trial`, "_blank")}
            >
              Start Free Trial
            </Button>
          </div>
          <MarketingInfo />
        </form>
      </section>
    </div>
  );
};

export default Login;
